import { ReactNode } from "react";

import { useIntl } from "react-intl";

import { PRICE_FINDER_CONTAINER } from "@/analytics/mappings/container.ts";
import { useTryIntent } from "@/components/try-intent/src/use-try-intent.ts";
import { useFirePricingPlanCtaEvent } from "@/libraries/design/components/pricing-card/src/pricing-card-cta-click.ts";
import { PricingCard } from "@/libraries/design/components/pricing-card/src/pricing-card.tsx";
import { DataEventProps } from "@/libraries/wpl/foundations/src/data-event-props";

import { useCalculationState } from "../calculation.ts";
import { useLocalizedCtaUrl } from "../components/localized-cta";
import { MonthlyEnterpriseMessage } from "../components/monthly-enterprise-message";
import { PriceAltContainer } from "../components/price-alt-container";
import { getFeaturesList } from "../components/features-list";
import { UserTier, UserTierExpanded } from "../components/user-tier";
import type { UserTierProps } from "../components/user-tier";
import { useBillingCycle, useSelector } from "../hooks.ts";
import * as messages from "../messages.ts";
import { getCurrencyIsoCode } from "../selectors.ts";
import { Plan } from "../types.ts";

export interface ProductPriceCardProps {
    planData: Plan;
    trackingProductKey: string;
    topSectionStyle?: React.CSSProperties;
    bottomSectionStyle?: React.CSSProperties;
    showBannerText?: boolean;
    customBannerText?: ReactNode;
    isSingleCard?: boolean;
    monthlyPerUserLabel?: string;
    tierLabelType?: UserTierProps["unitType"];
    omitRange?: boolean;
    hasRangeSlot?: boolean;
    priceAltElementForAboveMaxThreshold?: ReactNode;
}

type TrackingDataProductKey = DataEventProps["data-product-key"];

export interface UsePriceCardEventsProps {
    productKey: string;
    edition: string;
}

function usePriceCardEvents(props: UsePriceCardEventsProps) {
    const { productKey, edition } = props;
    const tryIntent = useTryIntent({
        containerId: PRICE_FINDER_CONTAINER,
        intentType: "button",
        productKey,
        edition
    });

    const handleClickCta = useFirePricingPlanCtaEvent({
        edition,
        productKey,
        containerId: PRICE_FINDER_CONTAINER
    });

    return {
        onClick: () => {
            tryIntent.onClick();
            handleClickCta();
        },
        onKeyDown: tryIntent.onKeyDown
    };
}

const FREE_EDITION = "free";

// the dataEventProps are added to the button props for these cards to automatically register
// analytics events, on top of the manual analytics events that we're calling; the events are
// duplicative, but we're keeping them for now to prevent regressions when comparing to the
// migrated page
export const FreePriceCard = (props: ProductPriceCardProps) => {
    const { planData, topSectionStyle, bottomSectionStyle, customBannerText } =
        props;
    const fields = planData.fields;
    const state = useCalculationState({ offeringSku: fields.sku });
    const intl = useIntl();
    const currencyIsoCode = useSelector(getCurrencyIsoCode);

    const cta = fields.microCta?.[0]?.fields;
    const ctaUrl = useLocalizedCtaUrl(cta);
    const eventHandlers = usePriceCardEvents({
        productKey: props.trackingProductKey,
        edition: FREE_EDITION
    });
    const dataEventPropsLabel = `${FREE_EDITION}-get-it-now`;

    const urlWithSignupSource = new URL(ctaUrl, window.location.origin);
    urlWithSignupSource.searchParams.set("signupSource", "pricing");
    const buttonHref = urlWithSignupSource.toString();

    return (
        <PricingCard
            hasBannerSlot
            topSectionStyle={topSectionStyle}
            bottomSectionStyle={bottomSectionStyle}
            bannerText={
                customBannerText ??
                (state.recommended && intl.formatMessage(messages.recommended))
            }
            title={fields.planTitleExternal}
            description={fields.planOneLiner}
            currencyIsoCode={currencyIsoCode}
            price={state.price}
            hasUnitSlot
            hasRangeSlot={
                props.hasRangeSlot !== undefined
                    ? props.hasRangeSlot
                    : state.billingCycle === "ANNUAL"
            }
            buttonText={cta?.ctaButtonText}
            buttonVariant={state.recommended ? "primary" : "secondary"}
            buttonHref={buttonHref}
            buttonProps={{
                onClick: eventHandlers.onClick,
                onKeyDown: eventHandlers.onKeyDown,
                dataEventProps: {
                    "data-product-key":
                        props.trackingProductKey as TrackingDataProductKey,
                    "data-event-component": "linkButton",
                    "data-event-container": PRICE_FINDER_CONTAINER,
                    "data-event": "clicked",
                    "data-label": `${dataEventPropsLabel}`,
                    "data-label-english": `${dataEventPropsLabel}`
                }
            }}
            benefitsTitle={fields.highlightedFeaturesTitle}
            benefits={getFeaturesList(fields.highlightedFeaturesContainer)}
        />
    );
};

const STANDARD_EDITION = "standard";
const PREMIUM_EDITION = "premium";
export const StandardPriceCard = (props: ProductPriceCardProps) => (
    <CommonPriceCard {...props} edition={STANDARD_EDITION} />
);
export const PremiumPriceCard = (props: ProductPriceCardProps) => (
    <CommonPriceCard {...props} edition={PREMIUM_EDITION} />
);

interface CommonPriceCardProps extends ProductPriceCardProps {
    edition: string;
    priceAltElementForAboveMaxThreshold?: ReactNode;
}
// eslint-disable-next-line complexity
const CommonPriceCard = (props: CommonPriceCardProps) => {
    const {
        planData,
        topSectionStyle,
        bottomSectionStyle,
        tierLabelType,
        customBannerText
    } = props;
    const fields = planData.fields;
    const state = useCalculationState({ offeringSku: fields.sku });
    const intl = useIntl();
    const currencyIsoCode = useSelector(getCurrencyIsoCode);
    const dataEventPropsLabel = `${props.edition}-start-trial`;

    const cta = fields.microCta?.[0]?.fields;
    const ctaUrl = useLocalizedCtaUrl(cta);
    const eventHandlers = usePriceCardEvents({
        productKey: props.trackingProductKey,
        edition: props.edition
    });

    const priceAltElement = (() => {
        if (
            state.isUnitCountAboveMaxThreshold &&
            props.priceAltElementForAboveMaxThreshold !== undefined
        ) {
            return props.priceAltElementForAboveMaxThreshold;
        }
        return null;
    })();

    const urlWithSignupSource = new URL(ctaUrl, window.location.origin);
    urlWithSignupSource.searchParams.set("signupSource", "pricing");
    const buttonHref = urlWithSignupSource.toString();

    return (
        <PricingCard
            priceTestid={
                state.billingCycle === "ANNUAL"
                    ? "annual-price"
                    : "monthly-price"
            }
            hasRangeSlot={
                state.isUnitCountAboveMaxThreshold &&
                currencyIsoCode === "JPY" &&
                state.billingCycle === "ANNUAL"
            }
            hasBannerSlot
            topSectionStyle={topSectionStyle}
            bottomSectionStyle={bottomSectionStyle}
            bannerText={
                customBannerText ??
                (state.recommended && intl.formatMessage(messages.recommended))
            }
            title={fields.planTitleExternal}
            description={fields.planOneLiner}
            currencyIsoCode={currencyIsoCode}
            price={!state.isUnitCountAboveMaxThreshold && state.price}
            priceAltElement={
                state.isUnitCountAboveMaxThreshold &&
                props.priceAltElementForAboveMaxThreshold !== undefined ? (
                    priceAltElement
                ) : (
                    <PriceAltContainer>
                        {intl.formatMessage(
                            messages.aboveUnitMaxThreshold.default
                        )}
                    </PriceAltContainer>
                )
            }
            unit={
                state.isUnitCountAboveMaxThreshold
                    ? undefined
                    : state.billingCycle === "MONTHLY"
                    ? props.monthlyPerUserLabel ??
                      intl.formatMessage(messages.perUserPerMonth)
                    : intl.formatMessage(messages.perYear)
            }
            hasUnitSlot
            range={
                state.billingCycle === "ANNUAL" &&
                !props.omitRange &&
                !state.isUnitCountAboveMaxThreshold && (
                    <UserTier
                        // @ts-ignore
                        unitLimitLower={state.unitTierLowerBound}
                        // @ts-ignore
                        unitLimitUpper={state.unitTierUpperBound}
                        unitType={tierLabelType}
                    />
                )
            }
            tooltipContent={
                <UserTierExpanded
                    // @ts-ignore
                    unitLimitLower={state.unitTierLowerBound}
                    // @ts-ignore
                    unitLimitUpper={state.unitTierUpperBound}
                    unitsInTier={state.unitCount}
                    unitsAvailableInTier={state.unitsAvailableInTier}
                />
            }
            buttonText={cta?.ctaButtonText}
            buttonHref={buttonHref}
            buttonDisabled={state.isUnitCountAboveMaxThreshold}
            buttonVariant={
                state.recommended || props.isSingleCard
                    ? "primary"
                    : "secondary"
            }
            buttonProps={{
                onClick: eventHandlers.onClick,
                onKeyDown: eventHandlers.onKeyDown,
                dataEventProps: {
                    "data-product-key":
                        props.trackingProductKey as DataEventProps["data-product-key"],
                    "data-event-component": "linkButton",
                    "data-event-container": PRICE_FINDER_CONTAINER,
                    "data-event": "clicked",
                    "data-label": `${dataEventPropsLabel}`,
                    "data-label-english": `${dataEventPropsLabel}`
                }
            }}
            benefitsTitle={fields.highlightedFeaturesTitle}
            benefits={getFeaturesList(fields.highlightedFeaturesContainer)}
        />
    );
};

export interface EnterprisePriceCardProps extends ProductPriceCardProps {
    isJpyPriceEnabled?: boolean;
    priceAltElement?: ReactNode;
}
// eslint-disable-next-line complexity
export const EnterprisePriceCard = (props: EnterprisePriceCardProps) => {
    const {
        planData,
        topSectionStyle,
        bottomSectionStyle,
        showBannerText = true,
        isSingleCard,
        tierLabelType,
        customBannerText,
        isJpyPriceEnabled
    } = props;
    const fields = planData.fields;

    const state = useCalculationState({ offeringSku: fields.sku });
    const intl = useIntl();
    const currencyIsoCode = useSelector(getCurrencyIsoCode);

    const [_, changeBillingCycle] = useBillingCycle();

    const priceAltElement = (() => {
        if (props.priceAltElement !== undefined) {
            return props.priceAltElement;
        }

        if (currencyIsoCode === "JPY" && !isJpyPriceEnabled) {
            return (
                <PriceAltContainer>
                    {intl.formatMessage(messages.enterpriseJpyMessage)}
                </PriceAltContainer>
            );
        }

        if (state.billingCycle === "MONTHLY") {
            return (
                <PriceAltContainer style={{ paddingBlockEnd: "24px" }}>
                    <MonthlyEnterpriseMessage
                        switchToAnnual={() => changeBillingCycle("ANNUAL")}
                    />
                </PriceAltContainer>
            );
        }
        if (state.isUnitCountAboveMaxThreshold) {
            return (
                <PriceAltContainer style={{ paddingBlockEnd: "48px" }}>
                    {intl.formatMessage(
                        messages.aboveUnitMaxThreshold.cloudEnterprise
                    )}
                </PriceAltContainer>
            );
        }
        if (state.isUnitCountBelowMinThreshold) {
            return (
                <PriceAltContainer style={{ paddingBlockEnd: "48px" }}>
                    {intl.formatMessage(
                        messages.belowUnitMinThreshold.cloudEnterprise,
                        {
                            minUnitCount: state.unitCountMin
                        }
                    )}
                </PriceAltContainer>
            );
        }
        return null;
    })();

    const isPriceShown =
        !state.isUnitCountAboveMaxThreshold && !priceAltElement;

    let price: string | undefined = undefined;
    let range: ReactNode | undefined = undefined;
    let tooltipContent: ReactNode | undefined = undefined;
    if (isPriceShown) {
        price = state.price;
        range = (
            <UserTier
                // @ts-ignore
                unitLimitLower={state.unitTierLowerBound}
                // @ts-ignore
                unitLimitUpper={state.unitTierUpperBound}
                unitType={tierLabelType}
            />
        );
        tooltipContent = (
            <UserTierExpanded
                // @ts-ignore
                unitLimitLower={state.unitTierLowerBound}
                // @ts-ignore
                unitLimitUpper={state.unitTierUpperBound}
                unitsInTier={state.unitCount}
                unitsAvailableInTier={state.unitsAvailableInTier}
            />
        );
    }

    const cta = fields.microCta?.[0]?.fields;
    const ctaUrl = useLocalizedCtaUrl(cta);
    const edition = "enterprise";
    const eventHandlers = usePriceCardEvents({
        productKey: props.trackingProductKey,
        edition
    });
    const dataEventPropsLabel = `${edition}-contact-sales`;

    return (
        <PricingCard
            priceTestid={
                state.billingCycle === "ANNUAL"
                    ? "annual-price"
                    : "monthly-price"
            }
            hasBannerSlot
            hasRangeSlot={
                currencyIsoCode === "JPY" && state.billingCycle === "ANNUAL"
            }
            topSectionStyle={topSectionStyle}
            bottomSectionStyle={bottomSectionStyle}
            bannerText={
                customBannerText ??
                (showBannerText &&
                    state.recommended &&
                    intl.formatMessage(messages.recommended))
            }
            title={fields.planTitleExternal}
            description={fields.planOneLiner}
            currencyIsoCode={currencyIsoCode}
            price={price}
            priceAltElement={priceAltElement}
            unit={
                // if a price alt element exists, there should be no unit
                priceAltElement
                    ? undefined
                    : !state.isUnitCountAboveMaxThreshold &&
                      !state.isUnitCountBelowMinThreshold
                    ? intl.formatMessage(messages.perYear)
                    : undefined
            }
            range={range}
            hasUnitSlot={
                // the unbounded text for the price alt element makes the unbounded
                currencyIsoCode === "JPY"
            }
            tooltipContent={tooltipContent}
            buttonText={cta?.ctaButtonText}
            buttonHref={ctaUrl}
            buttonVariant={
                state.recommended || isSingleCard ? "primary" : "secondary"
            }
            buttonProps={{
                onClick: eventHandlers.onClick,
                onKeyDown: eventHandlers.onKeyDown,
                dataEventProps: {
                    "data-product-key":
                        props.trackingProductKey as TrackingDataProductKey,
                    "data-event-component": "linkButton",
                    "data-event-container": PRICE_FINDER_CONTAINER,
                    "data-event": "clicked",
                    "data-label": `${dataEventPropsLabel}`,
                    "data-label-english": `${dataEventPropsLabel}`
                }
            }}
            benefitsTitle={fields.highlightedFeaturesTitle}
            // in order for us to render just the top section fo a pricing card, the contentful data must be empty/undefined
            benefits={getFeaturesList(fields.highlightedFeaturesContainer)}
        />
    );
};
